import { Children, cloneElement, type ReactElement, useRef } from "react"

import { Popup, PopupProps } from "~/shared/ui/kit/components/Popup"
import { isMobileOrStandalone } from "~/shared/ui/constants"
import { usePopup } from "~/shared/ui/kit/hooks/usePopup"

/**
 * Добавляет подсказку `Popup` оборачиваемому элементу. Дочерний элемент должен поддерживать `ref`.
 * Затрет ref, если он передан в оборачиваемый элемент. Если нужно передавать ref, используйте хук usePopup и компонент
 * Popup отдельно.
 * Поддерживает все свойства `Popup`, кроме `targetElementRef` и `show`.
 * Не выводится на мобильных устройствах.
 * @param props
 * @component
 * @category Component
 * @example
 * ```tsx
 * import {PopupHint} from "ui/PopupHint";
 * import {Button} from "~/shared/ui/kit";
 *
 * function Example() {
 *     return (
 *         <PopupHint position="right" text="Подсказка для кнопки">
 *             <Button>ОК</Button>
 *         </PopupHint>
 *     )
 * }
 * ```
 */
export function PopupHint({
	children,
	text,
	...popupProps
}: {
	/** текст подсказки */
	text: string
	children: ReactElement<any>
} & Omit<PopupProps, "show" | "children" | "targetElementRef">) {
	const elRef = useRef<HTMLElement>(null)
	const popup = usePopup(elRef, 250)

	if (isMobileOrStandalone) return <>{children}</>

	const child = Children.only(children)

	return (
		<>
			<Popup {...popup} {...popupProps}>
				{text}
			</Popup>
			{cloneElement(child, { ref: elRef })}
		</>
	)
}
